<template>
  <div>
    <!-- Main Content of The Page -->
    <main role="main">
      <!-- Overlaying Buefy Loading Component -->
      <BLoading
        class="xb-loading"
        :is-full-page="false"
        :active.sync="isApplicationLoading"
        :can-cancel="false"
        style="z-index: 20;"
      />
    </main>
  </div>
</template>

<script>
// Utility and helper functions
const mapMutations = window.Vuex.mapMutations;

import baseService from "./../../services/index";
import Mgr from "../../services/securityService";
// node modules import
import jwt_decode from "jwt-decode";

export default {
  name: "OidcCallback",
  data() {
    return {
      mgr: null,
      isApplicationLoading: true,
      redirectionPath: ""
    };
  },
  mounted() {
    if (!this.$store.state.auth.signedIn) {
      const self = this;
      this.instantiateMgr();
      this.mgr
        .signinRedirectCallback()
        .then(user => {
          const decodedToken = jwt_decode(user.access_token);
          this.$store.commit("header/setIsValidated", true);
          this.setLoginTime(
            decodedToken.iat
              ? decodedToken.iat * 1000
              : new Date(user.profile.auth_time * 1000).getTime()
          );
          this.setSignedIn(true);
          this.setAccessToken(user.access_token);
          this.setAdditionalProfileProperties(user.profile);
          this.setUserName(user.profile.nric || user.profile.identity_number);
          this.setIsSessionActive(true);
          this.setRoles(
            typeof user.profile.roles === "string"
              ? [user.profile.roles]
              : user.profile.roles
              ? [...user.profile.roles]
              : []
          );
          this.setOriginToken({
            accessToken: user.access_token,
            refreshToken: user.refresh_token
          });
          this.mgr.clearStaleState();
          if (
            user.profile.realm_roles &&
            user.profile.realm_roles.includes("impersonate")
          )
            this.setCanImpersonate(true);
          this.redirectionPath = user.state.path;
          this.fetchProfileValidation();
        })
        .catch(function(err) {
          const customizedErrorMessage = [
            "iat is in the future",
            "exp is in the future",
            "nbf is in the future",
            "iat is in the past",
            "exp is in the past",
            "nbf is in the past"
          ];
          let isCustomizedErrorMessageAppeared = false;

          customizedErrorMessage.forEach(message => {
            if (err.message.includes(message)) {
              isCustomizedErrorMessageAppeared = true;
            }
          });

          if (isCustomizedErrorMessageAppeared) {
            self.setSignedIn(true);
            self.setIsLoginError(true);
            self.isApplicationLoading = false;
            self.$buefy.dialog.alert({
              message: `Please sync your machine's date and time to the current time, then try to re-login. 
              </br></br>
              To sync to the current time, click this <a style="text-decoration: underline;color: #145fac;" href="https://support.microsoft.com/en-us/windows/how-to-set-your-time-and-time-zone-dfaa7122-479f-5b98-2a7b-fa0b6e01b261">link</a> if you are using Windows OS or this <a style="text-decoration: underline;color: #145fac;" href="https://support.apple.com/en-sg/guide/mac-help/mchlp2996/mac">link</a> if you are using macOS.`,
              confirmText: "OK",
              type: "is-danger",
              hasIcon: true,
              onConfirm: () => {
                self.$store.commit("auth/setIsManuallySignedOut", true);
                self.mgr
                  .signoutRedirect()
                  .then(function() {
                    self.resetStoreAuthState();
                  })
                  .catch(function() {
                    self.resetStoreAuthState();
                  });
              }
            });
          } else {
            self.isApplicationLoading = false;
            self.$buefy.dialog.alert({
              message: err.message,
              confirmText: "OK",
              type: "is-danger",
              hasIcon: true
            });

            self.$store.commit("auth/setIsManuallySignedOut", true);
            self.mgr
              .signoutRedirect()
              .then(function() {
                self.resetStoreAuthState();
              })
              .catch(function() {
                self.resetStoreAuthState();
              });
            self.setAuthErrors(err.message);
          }
        });
    } else if (this.$store.state.auth.isLoginError) {
      this.instantiateMgr();
      this.$store.commit("auth/setIsManuallySignedOut", true);
      this.mgr
        .signoutRedirect()
        .then(function() {
          this.resetStoreAuthState();
        })
        .catch(function() {
          this.resetStoreAuthState();
        });
    } else {
      this.$router.push({ path: "/" });
    }
  },
  methods: {
    ...mapMutations({
      setSignedIn: "auth/setSignedIn",
      setLoginTime: "auth/setLoginTime",
      setIsSessionActive: "auth/setIsSessionActive",
      setAuthErrors: "auth/setAuthErrors",
      setOriginToken: "auth/setOriginToken",
      setAccessToken: "auth/setAccessToken",
      setIsLoginError: "auth/setIsLoginError",
      setRoles: "header/setRoles",
      setUserName: "header/setUserName",
      setAdditionalProfileProperties: "header/setAdditionalProfileProperties",
      setCanImpersonate: "header/setCanImpersonate"
    }),

    resetStoreAuthState() {
      this.$store.commit("auth/setSignedIn", false);
      this.$store.commit("auth/setAccessToken", "");
      this.$store.commit("auth/setIsReminded", false);
      this.$store.commit("auth/setIsSessionActive", false);
      this.$store.commit("auth/setWasIdle", false);
      this.$store.commit("auth/setLoginTime", null);
      this.$store.commit("header/setUserName", "");
      this.$store.commit("header/setCanImpersonate", false);
      this.$store.commit("header/setIsImpersonating", false);
      this.$store.commit("header/setRoles", []);
      this.$store.commit("auth/setIsLoginError", false);
    },

    instantiateMgr() {
      this.mgr = new Mgr(
        this.$store.state.config.configurationData.common_module
      ).mgr;
    },

    fetchProfileValidation() {
      if (
        this.$store.state.header.userProfile.roles.includes("pii-brokers") &&
        this.$store.state.header.userProfile.profileType == 1
      ) {
        this.$store.commit("header/setHasProfileTo", true);
        this.fetchPIIBrokersOrAEAName("/profile/pii-broker", "/PII-management");
      } else if (this.$store.state.header.userProfile.roles.includes("aea")) {
        this.$store.commit("header/setHasProfileTo", true);
        this.fetchPIIBrokersOrAEAName("/exam/profiles", "/RES-exam-management");
      } else {
        this.fetchInfoFromProfileManagement();
      }
    },

    handleFetchProfileValidationSuccess() {
      return response => {
        const hasProfile =
          response.data.data.id &&
          !response.data.data.draftFlag &&
          response.data.data.modifiedByMopFlag
            ? true
            : false;

        if (hasProfile) {
          this.$store.commit("header/setHasProfileTo", true);
          // this.fetchInfoFromProfileManagement();

          if (this.redirectionPath) {
            this.$router.push({
              name: "Survey",
              query: { destination: this.redirectionPath }
            });
          } else {
            this.$router.push({ name: "Survey" });
          }
        } else {
          this.$store.commit("header/setHasProfileTo", false);
          if (
            this.redirectionPath &&
            this.redirectionPath.includes("/feedback-management")
          ) {
            this.$router.push({ path: this.redirectionPath });
          } else {
            this.$router.push({ path: "/profile-management" });
          }
        }
      };
    },

    handleFetchProfileValidationFailed() {
      this.$buefy.snackbar.open({
        duration: 4500,
        message: "Profile validation failed",
        type: "is-danger",
        position: "is-top",
        actionText: "✖"
      });

      this.$store.commit("header/setHasProfileTo", true);
      this.$store.commit("header/setIsValidated", false);

      this.$router.push({ path: "/access-denied" });
      // if (
      //   this.$store.state.header.userProfile.roles.includes("pii-brokers") &&
      //   this.$store.state.header.userProfile.profileType == 1
      // ) {
      //   this.$router.push({ path: "/PII-management" });
      // } else if (this.$store.state.header.userProfile.roles.includes("aea")) {
      //   this.$router.push({ path: "/RES-exam-management" });
      // } else if (this.redirectionPath) {
      //   this.$router.push({ path: this.redirectionPath });
      // } else {
      //   this.$router.push({ name: "Workspace" });
      // }
    },

    fetchInfoFromProfileManagement() {
      const endpoint = this.$store.state.config.configurationData.common_module
        .profileManagement.getProfileBySgid;

      baseService(
        endpoint,
        {},
        this.replaceUserName,
        this.profileValidationEndpoint,
        {
          headers: {
            Authorization: `Bearer ${this.$store.state.auth.accessToken}`
          }
        }
      );
    },

    replaceUserName(response) {
      const data = response.data.data;
      this.$store.commit("header/setAdditionalProfileInfo", data);
      const generalInfo = data.profileDetailDto.generalInfoJson;
      this.setUserName(
        generalInfo.name ||
          this.$store.state.header.userProfile.nric ||
          this.$store.state.header.userProfile.sgID
      );

      if (data.basicProfileFlag) {
        this.$store.commit("header/setHasProfileTo", true);
        if (this.redirectionPath) {
          this.$router.push({
            name: "Survey",
            query: { destination: this.redirectionPath }
          });
        } else {
          this.$router.push({ name: "Survey" });
        }
      } else {
        this.profileValidationEndpoint();
      }
    },

    profileValidationEndpoint() {
      const apiEndpoint = this.$store.state.config.configurationData
        .common_module;

      const payload = {
        sgId: this.$store.state.header.userProfile.sgID
      };

      baseService(
        apiEndpoint.profileManagement.getHasProfileValidationEndpoint,
        payload,
        this.handleFetchProfileValidationSuccess(),
        this.handleFetchProfileValidationFailed,
        {
          headers: {
            Authorization: `Bearer ${this.$store.state.auth.accessToken}`
          }
        }
      );
    },

    handleFetchDataFromProfileFailed(error) {
      this.$buefy.snackbar.open({
        duration: 4500,
        message: this.$_globalMixin_errorMessage(error),
        type: "is-danger",
        position: "is-top",
        actionText: "✖"
      });
    },

    fetchPIIBrokersOrAEAName(entityUri, redirectionTarget) {
      const endpoint = this.$store.state.config.configurationData.common_module
        .profileManagement.getProfileInfo;
      baseService(
        endpoint,
        { entityUri: entityUri },
        this.assignName(redirectionTarget),
        this.handleGetAEAorPIIBrokersNameError(redirectionTarget),
        {
          headers: {
            Authorization: `Bearer ${this.$store.state.auth.accessToken}`
          }
        }
      );
    },

    handleGetAEAorPIIBrokersNameError(redirectionTarget) {
      return e => {
        this.$buefy.snackbar.open({
          duration: 4500,
          message: this.$_globalMixin_errorMessage(e),
          type: "is-danger",
          position: "is-top",
          actionText: "✖"
        });
        this.$router.push({ path: redirectionTarget });
      };
    },

    assignName(redirectionTarget) {
      return response => {
        const data = response.data.data;
        const name = data.name;
        this.setUserName(
          name ||
            this.$store.state.header.userProfile.nric ||
            this.$store.state.header.userProfile.sgID
        );
        this.$router.push({ path: redirectionTarget });
      };
    }
  }
};
</script>
